import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Home, Briefcase, Bookmark, MapPin, Search, HelpCircle, ExternalLink, Check } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useMapStore } from '../../stores/mapStore';
import { savePlace, autocomplete, type AutocompleteResult } from '../../services/api';
import { formatPostcodeDisplay } from '../../utils/postcode';
import { MARKETING_URL, MAPBOX_TOKEN } from '../../lib/env';

interface AddPlaceModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultLabel?: 'home' | 'work' | 'saved';
  prefillName?: string;
  prefillAddress?: string;
  prefillCoords?: { lat: number; lng: number };
  prefillPostcode?: string;
  prefillPlaceId?: string;
}

// Build a full address string from autocomplete suggestion
function formatSuggestionAddress(s: AutocompleteResult): string {
  const parts: string[] = [];
  if (s.text) parts.push(s.text);
  if (s.street && !s.text?.includes(s.street)) parts.push(s.street);
  if (s.district) parts.push(s.district);
  // Add full formatted postcode at end (with leading zeros stripped)
  const fullPc = formatPostcodeDisplay(s.postcode);
  if (fullPc) parts.push(fullPc);
  return parts.join(', ');
}

export function AddPlaceModal({ isOpen, onClose, defaultLabel = 'saved', prefillName, prefillAddress, prefillCoords, prefillPostcode, prefillPlaceId }: AddPlaceModalProps) {
  const { addSavedPlace, loadSavedPlaces } = useAuthStore();
  const { userLocation } = useMapStore();

  const [label, setLabel] = useState<'home' | 'work' | 'saved'>(defaultLabel);
  const [name, setName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [address, setAddress] = useState('');
  const [selectedPostcode, setSelectedPostcode] = useState('');
  const [useCurrentLocation, setUseCurrentLocation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<AutocompleteResult[]>([]);
  const [selectedCoords, setSelectedCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showFindPostcode, setShowFindPostcode] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mapbox static map token (same as app uses)
  const mapboxToken = MAPBOX_TOKEN;

  // Reset form when modal opens — prefill if data is provided (e.g. from bookmark)
  useEffect(() => {
    if (isOpen) {
      setLabel(defaultLabel);
      setName(prefillName || '');
      setSearchQuery(prefillName || '');
      setAddress(prefillAddress || '');
      setSelectedPostcode(prefillPostcode || '');
      setUseCurrentLocation(false);
      setError(null);
      setSuggestions([]);
      setSelectedCoords(prefillCoords || null);
      setShowFindPostcode(false);
    }
  }, [isOpen, defaultLabel, prefillName, prefillAddress, prefillCoords, prefillPostcode]);

  // Debounced search — accepts any query (name, address, postcode, etc.)
  const doSearch = useCallback(async (value: string) => {
    if (value.length < 2 || useCurrentLocation) {
      setSuggestions([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await autocomplete(value, {
        limit: 8,
        proximity: userLocation ? { latitude: userLocation.lat, longitude: userLocation.lng } : undefined,
      });
      setSuggestions(results.suggestions);
    } catch (err) {
      console.error('Search error:', err);
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, [useCurrentLocation, userLocation]);

  // Handle search input change with debounce
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setSelectedCoords(null);
    setAddress('');
    setSelectedPostcode('');

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (value.trim().length >= 2) {
        doSearch(value.trim());
      } else {
        setSuggestions([]);
      }
    }, 300);
  };

  // Handle suggestion selection — populate name, address, postcode, and coords
  const handleSelectSuggestion = (suggestion: AutocompleteResult) => {
    const fullAddress = formatSuggestionAddress(suggestion);
    // Short display format for the copyable badge (e.g. "A0V B448")
    const shortPc = suggestion.postcode_display || '';

    setSearchQuery(suggestion.text);
    setAddress(fullAddress);
    setSelectedPostcode(shortPc);
    setSelectedCoords(
      suggestion.coordinates
        ? { lat: suggestion.coordinates.latitude, lng: suggestion.coordinates.longitude }
        : null,
    );
    setSuggestions([]);

    // Auto-fill name if empty
    if (!name.trim()) {
      setName(suggestion.text);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Please enter a place name.');
      return;
    }

    if (!useCurrentLocation && !selectedCoords) {
      setError('Please search and select an address from the results.');
      return;
    }

    setIsLoading(true);

    const coordinates = useCurrentLocation && userLocation
      ? userLocation
      : selectedCoords || { lat: 5.6037, lng: -0.1870 };

    const finalAddress = address.trim() || searchQuery.trim();

    try {
      const placeId = prefillPlaceId || `custom-${Date.now()}`;
      await savePlace({
        place_id: placeId,
        name: name.trim(),
        address: finalAddress,
        label,
        coordinates: { latitude: coordinates.lat, longitude: coordinates.lng },
      });

      addSavedPlace({
        name: name.trim(),
        address: finalAddress,
        coordinates,
        label,
        placeId: placeId,
      });

      loadSavedPlaces();
      onClose();
    } catch (err) {
      console.error('Failed to save place:', err);
      setError('Failed to save place. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-2xl shadow-xl dark:shadow-gray-900/50 w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800">
            <h2 className="text-lg font-semibold dark:text-white">Add Saved Place</h2>
            <button
              onClick={onClose}
              className="p-2 -mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Label Selection */}
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Label</label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setLabel('home')}
                  className={`p-3 rounded-xl border-2 transition-colors flex flex-col items-center gap-1 ${label === 'home'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                >
                  <Home size={20} className={label === 'home' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'} />
                  <span className={`text-sm ${label === 'home' ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-600 dark:text-gray-300'}`}>
                    Home
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setLabel('work')}
                  className={`p-3 rounded-xl border-2 transition-colors flex flex-col items-center gap-1 ${label === 'work'
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                >
                  <Briefcase size={20} className={label === 'work' ? 'text-orange-600 dark:text-orange-400' : 'text-gray-500 dark:text-gray-400'} />
                  <span className={`text-sm ${label === 'work' ? 'text-orange-600 dark:text-orange-400 font-medium' : 'text-gray-600 dark:text-gray-300'}`}>
                    Work
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setLabel('saved')}
                  className={`p-3 rounded-xl border-2 transition-colors flex flex-col items-center gap-1 ${label === 'saved'
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                >
                  <Bookmark size={20} className={label === 'saved' ? 'text-purple-600 dark:text-purple-400' : 'text-gray-500 dark:text-gray-400'} />
                  <span className={`text-sm ${label === 'saved' ? 'text-purple-600 dark:text-purple-400 font-medium' : 'text-gray-600 dark:text-gray-300'}`}>
                    Saved
                  </span>
                </button>
              </div>
            </div>

            {/* Name Input */}
            <div>
              <label htmlFor="place-name" className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">
                Place Name
              </label>
              <input
                id="place-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={label === 'home' ? 'My Home' : label === 'work' ? 'My Office' : 'Place name'}
                className="w-full px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-xl bg-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            {/* Address Search Input */}
            <div className="relative z-20">
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="place-search" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Search Address
                </label>
                <button
                  type="button"
                  onClick={() => setShowFindPostcode(!showFindPostcode)}
                  className="text-xs text-primary-500 hover:text-primary-600 dark:text-primary-400 dark:hover:text-primary-300 flex items-center gap-1"
                >
                  <HelpCircle size={12} />
                  Find your postcode
                </button>
              </div>

              {/* Find your postcode helper */}
              {showFindPostcode && (
                <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-sm">
                  <p className="text-blue-700 dark:text-blue-300 mb-2">
                    Don't know your postcode? You can find it by:
                  </p>
                  <ul className="space-y-1.5 text-blue-600 dark:text-blue-400 text-xs">
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5">1.</span>
                      <span>Search for your location on the main map using the search bar</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5">2.</span>
                      <span>Tap on a building near your location — the postcode will be shown in the details</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-0.5">3.</span>
                      <span>Copy the postcode and paste it here</span>
                    </li>
                  </ul>
                  <a
                    href={MARKETING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline font-medium"
                  >
                    <ExternalLink size={11} />
                    Visit coordal.com to look up postcodes
                  </a>
                </div>
              )}

              <div className="relative">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                <input
                  id="place-search"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search by name, address, or postcode..."
                  className={`w-full pl-11 pr-4 py-3 border rounded-xl bg-transparent text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 ${selectedCoords
                    ? 'border-green-300 dark:border-green-700'
                    : 'border-gray-200 dark:border-gray-700'
                    }`}
                  disabled={useCurrentLocation}
                />
                {selectedCoords && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500">
                    <Check size={16} strokeWidth={3} />
                  </div>
                )}
              </div>

              {/* Selected address + mini map preview */}
              {address && selectedCoords && (
                <div className="mt-2 space-y-2">
                  <div className="p-2.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <p className="text-xs font-medium text-green-700 dark:text-green-300">{address}</p>
                    {selectedPostcode && (
                      <span className="inline-flex items-center mt-1 bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 text-[11px] font-semibold px-1.5 py-0.5 rounded">
                        {selectedPostcode}
                      </span>
                    )}
                  </div>
                  {/* Mini map preview */}
                  {mapboxToken && (
                    <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                      <img
                        src={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-s+ef4444(${selectedCoords.lng},${selectedCoords.lat})/${selectedCoords.lng},${selectedCoords.lat},17,0/368x150@2x?access_token=${mapboxToken}`}
                        alt="Location preview"
                        className="w-full h-[150px] object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Search suggestions dropdown */}
              {suggestions.length > 0 && !useCurrentLocation && (
                <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-xl shadow-xl dark:shadow-gray-900/50 border border-gray-200 dark:border-gray-700 max-h-64 overflow-y-auto z-50">
                  {suggestions.map((s, i) => {
                    const displayCode = s.postcode_display || formatPostcodeDisplay(s.postcode) || '';
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => handleSelectSuggestion(s)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-0 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <MapPin size={16} className="text-primary-500 dark:text-primary-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{s.text}</p>
                            {(s.address || s.street || s.district) && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                {[s.address || s.street, s.district].filter(Boolean).join(', ')}
                              </p>
                            )}
                            {displayCode && (
                              <div className="flex items-center gap-1.5 mt-1">
                                <span className="inline-flex items-center bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 text-[11px] font-semibold px-1.5 py-0.5 rounded">
                                  {displayCode}
                                </span>
                                {s.ghana_post && (
                                  <span className="inline-flex items-center bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-[11px] px-1.5 py-0.5 rounded">
                                    {s.ghana_post}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {isSearching && (
                <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-xl shadow-xl dark:shadow-gray-900/50 border border-gray-200 dark:border-gray-700 p-4 text-center z-50">
                  <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto" />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Searching...</p>
                </div>
              )}
            </div>

            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Use Current Location */}
            {userLocation && (
              <button
                type="button"
                onClick={() => {
                  setUseCurrentLocation(!useCurrentLocation);
                  if (!useCurrentLocation) {
                    setSearchQuery('');
                    setAddress('Current Location');
                    setSelectedCoords(userLocation);
                    setSelectedPostcode('');
                    setSuggestions([]);
                  } else {
                    setAddress('');
                    setSelectedCoords(null);
                    setSelectedPostcode('');
                  }
                }}
                className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-colors ${useCurrentLocation
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
              >
                <MapPin size={20} className={useCurrentLocation ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'} />
                <span className={useCurrentLocation ? 'text-primary-600 dark:text-primary-400 font-medium' : 'text-gray-600 dark:text-gray-300'}>
                  Use current location
                </span>
              </button>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !name.trim() || (!useCurrentLocation && !selectedCoords)}
              className="w-full bg-primary-500 text-white py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Bookmark size={18} />
                  Save Place
                </>
              )}
            </button>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
